import useCurrentUser from '@hooks/useCurrentUser'

import { digObject } from '@domain-group/javascript-utils'

import Agencies, {
  configOptions as agenciesConfigOptions,
  icon as AgencyIcon,
} from '../components/DashboardModules/Agencies'

import CustomLinks, {
  configOptions as customLinksConfigOptions,
  icon as CustomLinksIcon,
} from '../components/DashboardModules/CustomLinks'

import DashboardSearchBox, {
  configOptions as dashboardSearchBoxConfigOptions,
  icon as DashboardSearchBoxIcon,
} from '../components/DashboardModules/DashboardSearchBox'

import FailedApiLogs, {
  configOptions as failedApiLogsConfigOptions,
  icon as failedApiLogsIcon,
} from '../components/DashboardModules/FailedApiLogs'

import RecentCampaigns, {
  configOptions as recentCampaignsConfigOptions,
  icon as RecentCampaignsIcon,
} from '../components/DashboardModules/RecentCampaigns'

import PlatformLinks, {
  configOptions as platformLinksConfigOptions,
  icon as platformLinksIcon,
} from '../components/DashboardModules/PlatformLinks'

import UpcomingPaginations, {
  configOptions as upcomingPaginationsConfigOptions,
  icon as upcomingPaginationsIcon,
} from '../components/DashboardModules/UpcomingPaginations'

export const generateAvailableModules = (currentUserPayload: ReturnType<typeof useCurrentUser>) => {
  const { isAdmin, isBrandUser } = currentUserPayload

  const customisableDashboardModules = {
    Agencies: {
      component: Agencies,
      configOptions: agenciesConfigOptions(),
      icon: AgencyIcon,
      id: 'Agencies',
      isSelectable: isBrandUser,
      title: 'Agencies',
    },
    CustomLinks: {
      component: CustomLinks,
      configOptions: customLinksConfigOptions(),
      icon: CustomLinksIcon,
      id: 'CustomLinks',
      isSelectable: true,
      title: 'Custom Links',
    },
    DashboardSearchBox: {
      component: DashboardSearchBox,
      configOptions: dashboardSearchBoxConfigOptions(),
      icon: DashboardSearchBoxIcon,
      id: 'DashboardSearchBox',
      isSelectable: true,
      title: 'Dashboard Search',
    },
    FailedApiLogs: {
      component: FailedApiLogs,
      configOptions: failedApiLogsConfigOptions(),
      icon: failedApiLogsIcon,
      id: 'FailedApiLogs',
      isSelectable: isAdmin,
      title: 'Failed API Calls',
    },
    PlatformLinks: {
      component: PlatformLinks,
      configOptions: platformLinksConfigOptions(),
      icon: platformLinksIcon,
      id: 'PlatformLinks',
      isSelectable: isAdmin,
      title: 'Platform Links',
    },
    RecentCampaigns: {
      component: RecentCampaigns,
      configOptions: recentCampaignsConfigOptions(),
      icon: RecentCampaignsIcon,
      id: 'RecentCampaigns',
      isSelectable: isBrandUser,
      title: 'Recent Campaigns',
    },
    UpcomingPaginations: {
      component: UpcomingPaginations,
      configOptions: upcomingPaginationsConfigOptions(),
      icon: upcomingPaginationsIcon,
      id: 'UpcomingPaginations',
      isSelectable: isAdmin,
      title: 'Upcoming Paginations',
    },
  }

  // @ts-expect-error TS2344
  const filteredModules: ReturnType<typeof customisableDashboardModules> = {}

  Object.keys(customisableDashboardModules).forEach((module) => {
    // @ts-expect-error TS7053
    const targetModule = customisableDashboardModules[module]

    if (targetModule.isSelectable){
      filteredModules[module] = targetModule
    }
  })

  return filteredModules
}

type DefaultModule = {
  key: string,
  moduleId: string,
  isAllowed?: boolean,
}

export const dashboardDefaultLayout = (currentUserPayload: ReturnType<typeof useCurrentUser>) => {
  const { isAdmin, isBrandUser } = currentUserPayload

  const defaultLayout = {
    columns: {
      main: {
        id: 'main',
        modules: [
          {
            key: 'Agencies-1',
            moduleId: 'Agencies',
            isAllowed: true,
          },
          {
            key: 'DashboardSearchBox-1',
            moduleId: 'DashboardSearchBox',
            isAllowed: true,
          },
          {
            key: 'FailedApiLogs-1',
            moduleId: 'FailedApiLogs',
            isAllowed: isAdmin,
          },
          {
            key: 'RecentCampaigns-1',
            moduleId: 'RecentCampaigns',
            isAllowed: isBrandUser,
          },
          {
            key: 'UpcomingPaginations-1',
            moduleId: 'UpcomingPaginations',
            isAllowed: isAdmin,
          },
        ],
      },
      sidebar: {
        id: 'sidebar',
        modules: [
          {
            key: 'PlatformLinks-1',
            moduleId: 'PlatformLinks',
            isAllowed: isAdmin,
          },
        ],
      },
    },
    custom: false,
    options: {
      'FailedApiLogs-1': {
        queryParams: {},
      },
      'PlatformLinks-1': {
        queryParams: {},
      },
      'UpcomingPaginations-1': {
        queryParams: {},
      },
    },
  }

  const filteredDefaultLayout = JSON.parse(JSON.stringify(defaultLayout))

  const defaultModulesMain: DefaultModule[] = digObject(defaultLayout, 'columns.main.modules', {})
  defaultModulesMain.forEach((module, index) => {
    if (!module.isAllowed){
      filteredDefaultLayout.columns.main.modules.splice(index, 1)
    }
  })

  const defaultModulesSideBar: DefaultModule[] = digObject(filteredDefaultLayout, 'columns.sidebar.modules', {})
  defaultModulesSideBar.forEach((module, index) => {
    if (!module.isAllowed){
      filteredDefaultLayout.columns.sidebar.modules.splice(index, 1)
    }
  })
  return filteredDefaultLayout
}
